/**
 * Custom Analytics Tracker
 * Batched event collection with sendBeacon fallback.
 * Additive to existing Google Analytics — does not replace it.
 */

const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 10;

let _config = { apiUrl: '', debug: false };
let _queue = [];
let _sessionId = null;
let _visitorId = null;
let _userId = null;
let _orgId = null;
let _initialized = false;
let _flushTimer = null;
let _pageCount = 0;
let _sessionStartTime = null;

// ── Helpers ────────────────────────────────────────────────────────────

function _getOrCreateVisitorId() {
  let id = localStorage.getItem('_lw_vid');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('_lw_vid', id);
  }
  return id;
}

function _getOrCreateSessionId() {
  let id = sessionStorage.getItem('_lw_sid');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('_lw_sid', id);
  }
  return id;
}

function _getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function _getScreenResolution() {
  return `${window.screen.width}x${window.screen.height}`;
}

function _getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function _getLocale() {
  return navigator.language || navigator.userLanguage || 'en';
}

function _getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
}

function _log(...args) {
  if (_config.debug) {
    console.log('[Analytics]', ...args);
  }
}

// ── Core ───────────────────────────────────────────────────────────────

function init({ apiUrl, debug = false }) {
  if (_initialized) return;

  _config = { apiUrl, debug };
  _visitorId = _getOrCreateVisitorId();
  _sessionId = _getOrCreateSessionId();
  _sessionStartTime = Date.now();
  _pageCount = 0;
  _initialized = true;

  // Start flush timer
  _flushTimer = setInterval(_flush, FLUSH_INTERVAL_MS);

  // Send session start
  _sendSessionStart();

  // Flush on page hide (tab close, navigation away)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      _sendSessionEnd();
      _flushBeacon();
    }
  });

  _log('Initialized', { apiUrl, sessionId: _sessionId, visitorId: _visitorId });
}

function identify(userId, orgId) {
  _userId = userId;
  _orgId = orgId;
  _log('Identified', { userId, orgId });
}

function reset() {
  _userId = null;
  _orgId = null;
  _log('Reset identity');
}

// ── Tracking Methods ───────────────────────────────────────────────────

function pageView(page, title) {
  if (!_initialized) return;
  _pageCount++;
  _enqueue({
    event_type: 'pageview',
    page_url: page,
    label: title || document.title,
  });
}

function event({ category, action, label, properties }) {
  if (!_initialized) return;
  _enqueue({
    event_type: 'feature_use',
    category,
    action,
    label,
    properties,
    page_url: window.location.pathname,
  });
}

function click({ category, action, label, properties }) {
  if (!_initialized) return;
  _enqueue({
    event_type: 'click',
    category,
    action,
    label,
    properties,
    page_url: window.location.pathname,
  });
}

function conversion({ category, action, label, properties }) {
  if (!_initialized) return;
  _enqueue({
    event_type: 'conversion',
    category,
    action,
    label,
    properties,
    page_url: window.location.pathname,
  });
}

// ── Queue Management ───────────────────────────────────────────────────

function _enqueue(eventData) {
  _queue.push({
    ...eventData,
    session_id: _sessionId,
    visitor_id: _visitorId,
    referrer: document.referrer || null,
    screen_resolution: _getScreenResolution(),
    locale: _getLocale(),
    device_type: _getDeviceType(),
    timestamp: new Date().toISOString(),
  });

  if (_queue.length >= MAX_QUEUE_SIZE) {
    _flush();
  }
}

async function _flush() {
  if (_queue.length === 0) return;

  const events = [..._queue];
  _queue = [];

  const payload = {
    events,
    session_id: _sessionId,
    source: 'app',
  };

  try {
    const response = await fetch(`${_config.apiUrl}/batch`, {
      method: 'POST',
      headers: _getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      // Put events back on failure (retry once)
      _queue.unshift(...events);
      _log('Flush failed, re-queued', response.status);
    } else {
      _log('Flushed', events.length, 'events');
    }
  } catch (err) {
    // On network error, try to re-queue
    _queue.unshift(...events);
    _log('Flush error', err.message);
  }
}

function _flushBeacon() {
  if (_queue.length === 0) return;

  const events = [..._queue];
  _queue = [];

  const payload = {
    events,
    session_id: _sessionId,
    source: 'app',
  };

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(`${_config.apiUrl}/batch`, blob);
    _log('Beacon sent', events.length, 'events');
  } catch (err) {
    _log('Beacon failed', err.message);
  }
}

// ── Session Management ─────────────────────────────────────────────────

async function _sendSessionStart() {
  const utmParams = _getUTMParams();
  const payload = {
    session_id: _sessionId,
    visitor_id: _visitorId,
    entry_page: window.location.pathname,
    referrer: document.referrer || null,
    ...utmParams,
    locale: _getLocale(),
    device_type: _getDeviceType(),
    browser: _getBrowser(),
    os: _getOS(),
    screen_resolution: _getScreenResolution(),
    source: 'app',
  };

  try {
    await fetch(`${_config.apiUrl}/session/start`, {
      method: 'POST',
      headers: _getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    _log('Session started');
  } catch (err) {
    _log('Session start error', err.message);
  }
}

function _sendSessionEnd() {
  const duration = Math.round((Date.now() - _sessionStartTime) / 1000);
  const payload = {
    session_id: _sessionId,
    exit_page: window.location.pathname,
    duration_seconds: duration,
    page_count: _pageCount,
  };

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(`${_config.apiUrl}/session/end`, blob);
    _log('Session end beacon sent', { duration, pageCount: _pageCount });
  } catch (err) {
    _log('Session end error', err.message);
  }
}

function _getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
}

function _getOS() {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
}

// ── Exports ────────────────────────────────────────────────────────────

const tracker = {
  init,
  identify,
  reset,
  pageView,
  event,
  click,
  conversion,
  flush: _flush,
};

export default tracker;
